import { useEffect, useState } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { TimeInput } from './TimeInput';
import { DayInput } from './DayInput';
import { useLockBodyScroll } from './useLockBodyScroll';

const animatedComponents = makeAnimated();

const toLocalIsoDate = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const toTimeString = (date) => {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const timeStringToMinutes = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

const buildOccurrenceIso = (dateIso, timeString) => {
  const dateMatch = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(dateIso || '');
  const timeMatch = /^([0-9]{2}):([0-9]{2})$/.exec(timeString || '');

  if (!dateMatch || !timeMatch) {
    return undefined;
  }

  const year = Number.parseInt(dateMatch[1], 10);
  const month = Number.parseInt(dateMatch[2], 10);
  const day = Number.parseInt(dateMatch[3], 10);
  const hours = Number.parseInt(timeMatch[1], 10);
  const minutes = Number.parseInt(timeMatch[2], 10);

  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return undefined;
  }

  const localDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
  if (Number.isNaN(localDateTime.getTime())) {
    return undefined;
  }
  return localDateTime.toISOString();
};

const buildSendNowOccurrenceIso = () => {
  const scheduledDate = new Date(Date.now() + 30 * 1000);
  if (
    scheduledDate.getSeconds() !== 0 ||
    scheduledDate.getMilliseconds() !== 0
  ) {
    scheduledDate.setMinutes(scheduledDate.getMinutes() + 1);
  }
  scheduledDate.setSeconds(0, 0);
  return scheduledDate.toISOString();
};

export default function OneTimeEventModal({
  event,
  langUrl,
  isOpen,
  onClose,
  onSave,
  onDelete,
  demoMode = false,
  trads,
}) {
  const [content, setContent] = useState('');
  const [sendNow, setSendNow] = useState(true);
  const [date, setDate] = useState(undefined);
  const [dateDefaultValue, setDateDefaultValue] = useState(undefined);
  const [time, setTime] = useState(undefined);
  const [timeDefaultValue, setTimeDefaultValue] = useState(undefined);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const t = trads?.index?.oneTimeModal || {};
  const tContacts = trads?.contacts || {};
  const isUpdateMode = Boolean(event?.id);

  useLockBodyScroll(isOpen);

  const setFieldsToNow = () => {
    const now = new Date();
    const nowDate = toLocalIsoDate(now);
    setDate(nowDate);
    setDateDefaultValue(nowDate);
    const nowTimeString = toTimeString(now);
    setTime(nowTimeString);
    setTimeDefaultValue(now.getHours() * 60 + now.getMinutes());
  };

  useEffect(() => {
    if (isOpen) {
      if (event) {
        setContent(event.content || '');
        const occurrenceIso =
          Array.isArray(event.incomingOccurrencesInUtc) &&
          event.incomingOccurrencesInUtc.length > 0
            ? [...event.incomingOccurrencesInUtc].sort()[0]
            : undefined;

        if (occurrenceIso) {
          const occurrenceDate = new Date(occurrenceIso);
          const localDate = toLocalIsoDate(occurrenceDate);
          setDate(localDate);
          setDateDefaultValue(localDate);
          setTime(toTimeString(occurrenceDate));
          setTimeDefaultValue(
            occurrenceDate.getHours() * 60 + occurrenceDate.getMinutes()
          );
          setSendNow(false);
        } else {
          setSendNow(true);
          setFieldsToNow();
        }

        const selected = (event.eventContacts || []).map((eventContact) => ({
          value: eventContact.contactId || eventContact.contact?.id,
          label: eventContact.contact?.name || eventContact.name,
        }));
        setSelectedContacts(selected);
        setSelectedGroupIds(event.groupIds || []);
      } else {
        setContent('');
        setSendNow(true);
        setSelectedContacts([]);
        setSelectedGroupIds([]);
        setFieldsToNow();
      }
    }
  }, [isOpen, event]);

  useEffect(() => {
    if (isOpen && !demoMode) {
      setLoadingContacts(true);
      fetch('/api/contacts/get-contacts')
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.contacts) {
            setContacts(data.contacts);
            setGroups(data.groups || []);
            if (data.contacts.length === 1) {
              setSelectedContacts([
                {
                  value: data.contacts[0].id,
                  label: data.contacts[0].name,
                },
              ]);
            }
          }
        })
        .catch((err) => console.error('Failed to fetch contacts:', err))
        .finally(() => setLoadingContacts(false));
    }
  }, [isOpen, demoMode]);

  if (!isOpen) {
    return null;
  }

  const contactOptions = contacts.map((contact) => ({
    value: contact.id,
    label: contact.name,
  }));
  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: `${group.name} (${group.count})`,
  }));
  const selectedGroupOptions = groupOptions.filter((group) =>
    selectedGroupIds.includes(group.value)
  );

  const handleNowToggle = (event) => {
    const checked = event.target.checked;
    setSendNow(checked);
    if (checked) {
      setFieldsToNow();
    }
  };

  const handleSave = () => {
    const now = new Date();
    const occurrenceIso = sendNow
      ? buildSendNowOccurrenceIso()
      : buildOccurrenceIso(date, time);

    if (!occurrenceIso) {
      return;
    }

    const timeInMinutes = sendNow
      ? now.getHours() * 60 + now.getMinutes()
      : timeStringToMinutes(time);

    onSave?.({
      id: event?.id || null,
      type: event?.type || 'oneTime',
      name: event?.name || t.defaultName || 'One-time message',
      content,
      days: [],
      daysInMonth: [],
      everyDays: null,
      times: [timeInMinutes],
      startsAt: null,
      stopsAt: null,
      incomingOccurrencesInUtc: [occurrenceIso],
      eventContacts: selectedContacts.map((selectedContact) => ({
        contactId: selectedContact.value,
      })),
      groupIds: selectedGroupIds,
    });
    onClose();
  };

  const handleDelete = () => {
    if (!event?.id || !onDelete) {
      return;
    }
    onDelete(event.id);
    onClose();
  };

  const isSaveDisabled =
    content.trim().length < 3 || (!sendNow && (!date || !time));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {isUpdateMode
              ? t.editTitle || t.title || 'Update one-time message'
              : t.title || 'Send or plan a message'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="one-time-modal-close-btn"
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.smsContent || 'SMS text'}
            </label>
            <textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              rows={4}
              data-testid="one-time-event-content-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={sendNow}
                onChange={handleNowToggle}
                data-testid="one-time-now-checkbox"
                className="h-4 w-4 rounded border-gray-300"
              />
              {t.now || 'Now'}
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.date || 'Date'}
            </label>
            <DayInput
              trads={trads}
              onChange={setDate}
              defaultValue={dateDefaultValue}
              disabled={sendNow}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.time || 'Time'}
            </label>
            <TimeInput
              trads={trads}
              onChange={setTime}
              defaultValue={timeDefaultValue}
              disabled={sendNow}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
              {t.contacts || 'Contacts'}
            </label>
            {demoMode ? (
              <div>
                {selectedContacts.length > 0 ? (
                  <div className="space-y-2">
                    {selectedContacts.map((contact) => (
                      <div
                        key={contact.value}
                        className="px-3 py-2 bg-green-50 border border-green-200 rounded-md text-green-800"
                      >
                        <div className="font-medium">{contact.label}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-500 text-sm">
                    {t.noContactsSelected || 'No contacts selected'}
                  </div>
                )}
              </div>
            ) : (
              <div data-testid="one-time-contacts-select">
                <Select
                  closeMenuOnSelect={false}
                  components={animatedComponents}
                  isMulti
                  options={contactOptions}
                  value={selectedContacts}
                  onChange={(values) => setSelectedContacts(values || [])}
                  isLoading={loadingContacts}
                  placeholder={t.selectContacts || 'Select contacts...'}
                  noOptionsMessage={() => tContacts.noContacts || 'No contacts'}
                  classNamePrefix="contacts-select"
                />
              </div>
            )}
            {contactOptions.length === 0 && (
              <div className="py-2 text-sm">
                <a
                  className="font-medium text-gray-900 underline hover:text-gray-700"
                  href={`/${langUrl}/contacts`}
                >
                  {t.addContacts || 'Add my first contacts'}
                </a>
              </div>
            )}
          </div>

          {!demoMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                {t.groups || 'Groups'}
              </label>
              <div data-testid="one-time-groups-select">
                <Select
                  closeMenuOnSelect={false}
                  components={animatedComponents}
                  isMulti
                  options={groupOptions}
                  value={selectedGroupOptions}
                  onChange={(values) =>
                    setSelectedGroupIds(
                      (values || []).map((group) => group.value)
                    )
                  }
                  placeholder={t.selectGroups || 'Select groups...'}
                  noOptionsMessage={() => tContacts.noGroups || 'No groups'}
                  classNamePrefix="contacts-select"
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-between gap-3">
          {isUpdateMode && onDelete ? (
            <button
              type="button"
              onClick={handleDelete}
              data-testid="one-time-event-delete-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              {t.delete || tContacts.delete || 'Delete'}
            </button>
          ) : (
            <div></div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              data-testid="one-time-event-cancel-btn"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              {t.cancel || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaveDisabled}
              data-testid="one-time-event-save-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.save || 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
